/**
 * ReverseReceiptDialog — BLU-3xx Phase 47.01.2-B-05
 *
 * Two-click confirmation for reversing a receipt from the Receive History
 * page. Per CONTEXT.md FE-8, single-click reverse is too easy to misclick on
 * a transparency surface; the AlertDialog gives the operator a beat to
 * read the receipt back before committing.
 *
 * The destructive styling is reserved for the confirm BUTTON inside the
 * dialog — the row-level Reverse trigger stays calm (ghost variant w/
 * destructive-on-hover) so a screen full of rows doesn't read as a danger
 * zone.
 *
 * Idempotency note: `inventory.reverseReceipt` is server-side idempotent
 * (UNIQUE partial index on `reference_id WHERE reference_type =
 * 'receipt_reversal'`), so a double-click is a safe no-op. We don't add
 * client-side debouncing.
 */
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import type { ReceiveHistoryReceipt } from './ReceiveHistoryRow';
import { hexSlice } from '../utils/partnerFallback';

interface ReverseReceiptDialogProps {
  /** When set, the dialog is open and shows this receipt. null = closed. */
  receipt: ReceiveHistoryReceipt | null;
  ownerOrgName: string | null;
  actorOrgName: string | null;
  isPending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export function ReverseReceiptDialog({
  receipt,
  ownerOrgName,
  actorOrgName,
  isPending,
  onClose,
  onConfirm,
}: ReverseReceiptDialogProps) {
  const { t } = useTranslation('inventory');
  const open = receipt !== null;

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // While the mutation is in flight, ignore overlay clicks / Escape so
        // the user can't dismiss mid-RPC and end up confused about whether
        // the reversal landed.
        if (!next && !isPending) onClose();
      }}
    >
      <AlertDialogContent className="max-h-[80vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('receiveHistory.reverseDialog.title')}</AlertDialogTitle>
          <AlertDialogDescription className="text-sm">
            {t('receiveHistory.reverseDialog.body')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {receipt && (
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-sm rounded-md border bg-muted/30 p-3 my-1">
            <dt className="text-muted-foreground">{t('receiveHistory.reverseDialog.field.date')}</dt>
            <dd className="tabular-nums">
              {format(new Date(receipt.createdAt), 'PPpp')}
            </dd>

            <dt className="text-muted-foreground">{t('receiveHistory.reverseDialog.field.sku')}</dt>
            <dd className="font-mono">
              {receipt.sku ?? '-'}
              {receipt.skuTitle ? (
                <span className="ml-2 font-sans text-muted-foreground">
                  {receipt.skuTitle}
                </span>
              ) : null}
            </dd>

            {(receipt.skuColor || receipt.skuSize) && (
              <>
                <dt className="text-muted-foreground">{t('receiveHistory.reverseDialog.field.variant')}</dt>
                <dd>
                  {[receipt.skuColor, receipt.skuSize]
                    .filter(Boolean)
                    .join(' / ') || '-'}
                </dd>
              </>
            )}

            <dt className="text-muted-foreground">{t('receiveHistory.reverseDialog.field.quantity')}</dt>
            <dd className="tabular-nums font-medium">
              +{receipt.quantityChange}
            </dd>

            <dt className="text-muted-foreground">{t('receiveHistory.reverseDialog.field.owner')}</dt>
            <dd>
              {ownerOrgName ?? t('crossOrg.partnerFallbackHex', { hex: hexSlice(receipt.ownerOrganizationId) })}
            </dd>

            {actorOrgName &&
              receipt.actorOrganizationId &&
              receipt.actorOrganizationId !== receipt.ownerOrganizationId && (
                <>
                  <dt className="text-muted-foreground">{t('receiveHistory.reverseDialog.field.actor')}</dt>
                  <dd>{actorOrgName}</dd>
                </>
              )}
          </dl>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{t('receiveHistory.reverseDialog.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={(event) => {
              // Prevent the default "auto-close on action click" — we want the
              // dialog to stay open until the mutation resolves so a slow
              // network can't surprise the user with a vanished sheet.
              event.preventDefault();
              onConfirm();
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                {t('receiveUndo.reversing')}
              </>
            ) : (
              t('receiveHistory.reverseDialog.confirm')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
